import { Button, DatePicker, Input, message, Space, Spin, Table, Tag } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { serverFetch } from "@server";
import { fmtDateTimeFull } from "@extensions/utils";
import { strDate2Server } from "@extensions/convert";
import { refreshButton } from "@extensions/design";
import { tokenVidsLabel, messageStatusesRender, messageDirectionsRender } from "@extensions/enums";
import dayjs from "dayjs";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "./messages.css";
import MessagesItem from "./messages-item";

const STORAGE_KEY_FROM = "messages_list_dateFrom";
const STORAGE_KEY_TO = "messages_list_dateTo";

const loadSavedPeriod = () => {
  try {
    const from = localStorage.getItem(STORAGE_KEY_FROM);
    const to = localStorage.getItem(STORAGE_KEY_TO);
    if (from && to) {
      const dFrom = dayjs(from, "YYYY-MM-DD", true);
      const dTo = dayjs(to, "YYYY-MM-DD", true);
      if (dFrom.isValid() && dTo.isValid()) {
        return { dateFrom: dFrom, dateTo: dTo };
      }
    }
  } catch (e) {
    /* ignore */
  }
  return {
    dateFrom: dayjs().startOf("month"),
    dateTo: dayjs().endOf("day"),
  };
};

const MessagesList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => loadSavedPeriod().dateFrom);
  const [dateTo, setDateTo] = useState(() => loadSavedPeriod().dateTo);
  const [searchText, setSearchText] = useState("");
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const tableWrapRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(300);
  const isMobile = useViewportBreakpoint() === "mobile";

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftDateFrom, setDraftDateFrom] = useState(null);
  const [draftDateTo, setDraftDateTo] = useState(null);

  useEffect(() => {
    if (dateFrom) {
      try {
        localStorage.setItem(STORAGE_KEY_FROM, dateFrom.format("YYYY-MM-DD"));
      } catch (e) {
        /* ignore */
      }
    }
    if (dateTo) {
      try {
        localStorage.setItem(STORAGE_KEY_TO, dateTo.format("YYYY-MM-DD"));
      } catch (e) {
        /* ignore */
      }
    }
  }, [dateFrom, dateTo]);

  const getData = () => {
    setLoading(true);

    serverFetch(`message?dateFrom=${strDate2Server(dateFrom)}&dateTo=${strDate2Server(dateTo)}`)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- загрузка при смене периода
  }, [dateFrom, dateTo]);

  const filteredItems = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter(row => {
      const parts = [
        row.phone,
        row.body,
        row.errText,
        row.externalId,
        String(row.id ?? ""),
      ]
        .filter(v => v != null && String(v).trim() !== "")
        .map(v => String(v).toLowerCase());
      return parts.some(p => p.includes(q));
    });
  }, [items, searchText]);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (dateFrom && dateTo) {
      parts.push(`${dateFrom.format("DD.MM.YYYY")} — ${dateTo.format("DD.MM.YYYY")}`);
    } else if (dateFrom) parts.push(`с ${dateFrom.format("DD.MM.YYYY")}`);
    else if (dateTo) parts.push(`по ${dateTo.format("DD.MM.YYYY")}`);
    else parts.push("период не задан");
    const q = searchText.trim();
    if (q) parts.push(`поиск: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}`);
    return parts.join(" · ");
  }, [dateFrom, dateTo, searchText]);

  useEffect(() => {
    if (isMobile) return;
    const updateTableHeight = () => {
      const top = tableWrapRef.current?.getBoundingClientRect?.().top ?? 0;
      const viewportHeight = Math.min(window.innerHeight, document.documentElement.clientHeight);
      const next = Math.max(200, viewportHeight - top - 32);
      setTableScrollY(next);
    };

    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, [isMobile]);

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const openFilterModal = () => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (!draftDateFrom || !draftDateTo) {
      message.warning("Укажите период дат");
      return;
    }
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setFilterOpen(false);
  };

  const cancelFilter = () => setFilterOpen(false);

  const handleSendMessage = () => {
    setSendModalVisible(true);
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 80 },
    { dataIndex: "createdOn", title: "Дата создания", width: 180, render: fmtDateTimeFull },
    { dataIndex: "direction", title: "Направление", width: 120, render: messageDirectionsRender },
    { dataIndex: "phone", title: "Телефон", width: 150 },
    { dataIndex: "body", title: "Сообщение", width: 300 },
    { dataIndex: "vid", title: "Вид", width: 120, render: tokenVidsLabel },
    { dataIndex: "status", title: "Статус", width: 150, render: messageStatusesRender },
    { dataIndex: "errText", title: "Ошибка", ellipsis: true },
    { dataIndex: "externalId", title: "Внешний #", width: 120 },
  ];

  const tableEl = (
    <Table
      columns={columns}
      dataSource={filteredItems}
      loading={loading}
      rowKey="id"
      pagination={false}
      size="small"
      scroll={{ x: "max-content", y: tableScrollY }}
    />
  );

  const directionTag = dir => {
    if (dir === 1) {
      return (
        <Tag color="blue" className="messages-list-card-dir-tag">
          ВХ
        </Tag>
      );
    }
    if (dir === -1) {
      return (
        <Tag className="messages-list-card-dir-tag">
          ИСХ
        </Tag>
      );
    }
    return (
      <Tag className="messages-list-card-dir-tag">
        —
      </Tag>
    );
  };

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="messages-list-cards">
        {filteredItems.map(record => {
          const bodyText = record.body != null ? String(record.body) : "";
          const errText = record.errText != null ? String(record.errText).trim() : "";
          const extId = record.externalId != null ? String(record.externalId).trim() : "";
          return (
            <div key={record.id} className="messages-list-card">
              <div className="messages-list-card-row1">
                {directionTag(record.direction)}
                <span className="messages-list-card-id">#{record.id}</span>
                <span className="messages-list-card-date">{fmtDateTimeFull(record.createdOn) || "—"}</span>
                {record.phone != null && String(record.phone).trim() !== "" ? (
                  <span className="messages-list-card-phone">{String(record.phone).trim()}</span>
                ) : null}
              </div>
              <div className="messages-list-card-row2">{bodyText || "—"}</div>
              <div className="messages-list-card-row3">
                <span title="Вид">{tokenVidsLabel(record.vid) ?? "—"}</span>
                <span className="messages-list-card-sep" aria-hidden>
                  ·
                </span>
                <span title="Статус">{messageStatusesRender(record.status) ?? "—"}</span>
                <span className="messages-list-card-sep" aria-hidden>
                  ·
                </span>
                <span title="Ошибка" className="messages-list-card-err">
                  {errText || "—"}
                </span>
                <span className="messages-list-card-sep" aria-hidden>
                  ·
                </span>
                <span title="Внешний #" className="messages-list-card-ext">
                  {extId || "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  if (isMobile) {
    return (
      <div className="messages-list-page messages-list-page--mobile">
        <div className="messages-list-mobile-body">
          <div className="orders-mobile-toolbar">
            <div className="company-backups-mobile-search-row">
              <Input
                placeholder="По телефону, тексту, номеру, ошибке"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                allowClear
                className="company-backups-mobile-search-input"
              />
            </div>
            <div className="company-backups-mobile-filter-row">
              <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                {filterSummary}
              </span>
              <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                aria-label="Фильтр"
                className="company-backups-mobile-filter-icon-btn"
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={getData}
                loading={loading}
                disabled={!dateFrom || !dateTo}
                aria-label="Обновить"
                className="company-backups-mobile-refresh-btn"
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                disabled={loading}
                aria-label="Отправить"
                className="company-backups-mobile-refresh-btn"
              />
            </div>
          </div>
          <div className="orders-mobile-scroll messages-list-mobile-scroll">{mobileListEl}</div>
        </div>
        {filterOpen && (
          <div
            className="misoft-modal-overlay company-backups-filter-overlay--top"
            onClick={e => e.target === e.currentTarget && cancelFilter()}
          >
            <div
              className="misoft-modal-content company-backups-filter-sheet--top"
              onClick={e => e.stopPropagation()}
            >
              <div className="company-base-sheet-header">
                <span className="company-base-sheet-title">Фильтр</span>
                <Space size="small" className="company-base-sheet-actions">
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={applyFilter}
                    aria-label="Применить"
                    className="company-base-header-icon-btn"
                  />
                  <Button
                    type="default"
                    icon={<CloseOutlined />}
                    onClick={cancelFilter}
                    aria-label="Отмена"
                    className="company-base-header-icon-btn"
                  />
                </Space>
              </div>
              <div className="company-base-sheet-body company-backups-filter-sheet-body">
                <div className="company-backups-filter-field">
                  <span className="company-backups-filter-label">С даты</span>
                  <DatePicker
                    value={draftDateFrom}
                    onChange={setDraftDateFrom}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="company-backups-filter-field">
                  <span className="company-backups-filter-label">По дату</span>
                  <DatePicker
                    value={draftDateTo}
                    onChange={setDraftDateTo}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {sendModalVisible && <MessagesItem onClose={() => setSendModalVisible(false)} onSuccess={getData} />}
      </div>
    );
  }

  return (
    <div className="messages-list-page messages-list-page--desktop">
      <div className="page-cloud-row">
        <div>
          <div>Период с</div>
          <DatePicker value={dateFrom} onChange={e => setDateFrom(e)} format="DD.MM.YYYY" allowClear={false} style={{ width: 150 }} />
        </div>
        <div>
          <div>по</div>
          <DatePicker value={dateTo} onChange={e => setDateTo(e)} format="DD.MM.YYYY" allowClear={false} style={{ width: 150 }} />
        </div>
        <Input
          placeholder="По телефону, тексту, номеру, ошибке"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          allowClear
          style={{ width: 280 }}
        />
        <Button type="primary" onClick={handleSendMessage} disabled={loading}>
          Отправить
        </Button>
        {refreshButton(getData)}
      </div>
      <div ref={tableWrapRef} className="messages-list-desktop-table-wrap">
        {tableEl}
      </div>
      {sendModalVisible && <MessagesItem onClose={() => setSendModalVisible(false)} onSuccess={getData} />}
    </div>
  );
};

export default MessagesList;
